import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Container, Table, Button, Form, Modal, Offcanvas, Spinner, Alert } from 'react-bootstrap';
import './Statistics.css';
import SearchableSelect from '../components/SearchableSelect';
import useColumnWidths from '../components/useColumnWidths';
import { downloadBlob } from '../components/invoiceActions';
import { nactiObdobi, nactiSestavu, nactiCiselniky, maPrepis, naApi } from './statisticsApi';
import api from '../services/api';

/* ==========================================================================
   Statistiky → Provize (výpočtová sestava)

   Data jsou živá — sestavu počítá backend (`CommissionEngine`) a obrazovka
   je zobrazuje. Překlad mezi API a tvary, se kterými pracuje tohle JSX,
   dělá statisticsApi.js.

   CO ZATÍM NENÍ ULOŽITELNÉ: editace buněk, ruční položky a uzávěrka období
   žijí jen v paměti záložky, protože tabulky `commission_override`,
   `commission_manual_item` a `commission_period` ještě neexistují
   (etapa 4, viz plans/provize-statistiky-plan.md). Přepočet po editaci se
   proto dělá lokálně stejným vzorcem jako v enginu.

   ŘÁDEK = FAKTURA. Sestava ukazuje jeden řádek na uhrazený daňový doklad
   (zálohy, potvrzení platby ani dobropisy do provizí nevstupují). Provize
   se ale počítá po položkách, protože faktura může obsahovat víc vydání
   s různým pásmem — sazby na řádku jsou proto průměry vážené úhradou
   a jednotlivé položky jsou vidět v rozpadu výpočtu.
   ========================================================================== */

/* ------------------------------------------------------------ formátování */
const fmtKc = (v) => `${Math.round(v || 0).toLocaleString('cs-CZ')} Kč`;
const fmtNum = (v) => (v || 0).toLocaleString('cs-CZ');
const fmtPct = (v, dec = 1) =>
  `${((v || 0) * 100).toLocaleString('cs-CZ', { minimumFractionDigits: dec, maximumFractionDigits: dec })} %`;
const fmtPctRaw = (v, dec = 1) =>
  `${(v || 0).toLocaleString('cs-CZ', { minimumFractionDigits: dec, maximumFractionDigits: dec })} %`;

/* Sloupce sestavy na jednom místě — pořadí, popisek a klíč, pod kterým se
   řadí i pod kterým se uživateli ukládá šířka (`user_grid_state`, migrace 048).
   `min` je jen podklad pro první změření, dál si šířku určuje uživatel. */
const SLOUPCE = [
  /* Obchodník je v sestavě i u jednoho vybraného člověka: manažer má mezi
     řádky i faktury svého týmu (superprovize) a bez tohohle sloupce by
     nebylo poznat, čí řádek to je. Stejné pořadí má sestava z Contractu. */
  { klic: 'obchodnik', popisek: 'Obchodník', min: 150 },
  { klic: 'cisloFaktury', popisek: 'Č. faktury' },
  { klic: 'zakaznik', popisek: 'Zákazník / plátce', min: 220 },
  { klic: 'cisloInzeratu', popisek: 'Č. inzerátu' },
  { klic: 'vydaniNazev', popisek: 'Vydání', min: 190 },
  { klic: 'mutaceNazev', popisek: 'Mutace' },
  { klic: 'typMedia', popisek: 'Typ' },
  { klic: 'dpd', popisek: 'Dní po spl.', num: true },
  { klic: 'cenikovaCena', popisek: 'Ceníková cena', num: true },
  { klic: 'slevaPct', popisek: 'Sleva %', num: true },
  { klic: 'platbaBezDph', popisek: 'Úhrada bez DPH', num: true },
  { klic: 'j', popisek: '% z vydání', num: true },
  { klic: 'k', popisek: '% bonus', num: true },
  { klic: 'l', popisek: '% celkem', num: true },
  { klic: 'm', popisek: 'Provize Kč', num: true },
  { klic: 'akce', popisek: '', neradit: true, akce: true },
];
const SLOUPCE_KLICE = SLOUPCE.map(s => s.klic);
const SLOUPCE_MIN = SLOUPCE.reduce((acc, s) => (s.min ? { ...acc, [s.klic]: s.min } : acc), {});

const TYP_BADGE = {
  nase: { cls: 'b-nase', label: 'Naše médium', ikona: 'bi-newspaper' },
  partner: { cls: 'b-partner', label: 'Partnerské', ikona: 'bi-share' },
  special: { cls: 'b-special', label: 'Speciální', ikona: 'bi-box-seam' },
  /* faktura s položkami různých typů médií */
  mix: { cls: 'b-neutral', label: 'Kombinace', ikona: 'bi-intersect' },
};

/**
 * Dopočet provize nad jednou položkou.
 *   L = (J + K) × korekce za pozdní úhradu
 *   M = úhrada bez DPH × L
 * Ruční přepis (*Override) má vždy přednost před dopočtem.
 */
function dopocti(r, pasmoSplatnosti) {
  const pasmo = pasmoSplatnosti(r.dpd);
  const koef = pasmo.koef;
  const j = r.jOverride != null ? r.jOverride : r.jAuto;
  const k = r.kOverride != null ? r.kOverride : r.kAuto;
  const lAuto = (j + k) * koef;
  const l = r.lOverride != null ? r.lOverride : lAuto;
  const mAuto = r.platbaBezDph * l;
  const m = r.mOverride != null ? r.mOverride : mAuto;
  return {
    ...r,
    pasmo,
    koef,
    j,
    k,
    l,
    lAuto,
    m,
    mAuto,
    prepsano: r.jOverride != null || r.kOverride != null || r.lOverride != null || r.mOverride != null,
    ztrataKracenim: r.platbaBezDph * (j + k) * (1 - koef),
  };
}

/**
 * Dopočet superprovizního řádku.
 *
 * Sazba je pevná (1 % manažera regionu, 0,5 % obchodní ředitelky) a
 * **nekrátí se za opožděnou platbu** — v referenční sestavě od klienta dává
 * řádek 47 dní po splatnosti plný 1 %, i když obchodníkovi samotnému se
 * provize půlí. J a K zůstávají pro informaci, přepsat se nedají.
 */
function dopoctiSuper(r, pasmoSplatnosti) {
  const l = r.superPct || 0;
  return {
    ...r,
    pasmo: pasmoSplatnosti(r.dpd),
    koef: 1,
    j: r.jAuto,
    k: r.kAuto,
    l,
    lAuto: l,
    m: r.platbaBezDph * l,
    mAuto: r.platbaBezDph * l,
    prepsano: false,
  };
}

const soucet = (rs, fn) => rs.reduce((s, r) => s + (fn(r) || 0), 0);

/* Výchozí (nedotčené) hodnoty položek — proti nim se pozná ruční zásah.
   Plní se při načtení sestavy, ruční položky v nich nejsou (ty jsou
   odlišené podbarvením celého řádku). */
let PUVODNI = {};

/**
 * Byla hodnota ručně změněná?
 *
 * Dvě cesty: buď je přepis uložený na serveru (`r.prepis`, migrace 049) —
 * pak už je v hodnotě započítaný a proti `PUVODNI` se nepozná — nebo ho
 * uživatel právě teď zadal a do serveru ještě nedoputoval.
 */
const zmeneno = (r, pole) => {
  if (r.prepis && r.prepis[pole] != null) return true;
  const puv = PUVODNI[r.id];
  return !!puv && puv[pole] !== r[pole];
};

/** Kdo a kdy přepis uložil — do nápovědy buňky. */
const podpisPrepisu = (r) => {
  if (!r.prepis) return '';
  return [r.prepis.autor, r.prepis.kdy, r.prepis.duvod].filter(Boolean).join(' · ');
};

/** Popisek pro ručně změněnou buňku — ukáže původní hodnotu nebo autora. */
const titulekZmeny = (r, pole, format = (v) => v) => {
  if (!zmeneno(r, pole)) return '';
  if (r.prepis && r.prepis[pole] != null) {
    const kdo = [r.prepis.autor, r.prepis.kdy].filter(Boolean).join(' · ');
    return ['Ručně přepsáno', kdo, r.prepis.duvod].filter(Boolean).join(' · ');
  }
  return `Ručně změněno · výchozí hodnota ${format(PUVODNI[r.id][pole])}`;
};

/* Editovatelné pole → název, pod kterým přepis vrací server. */
const POLE_UI = { jOverride: 'j', kOverride: 'k', lOverride: 'l', mOverride: 'm' };

/* Pole obrazovky → pole, pod kterým přepis zná API (`statistics/override`). */
const POLE_PREPISU = {
  dpd: 'dpd',
  cenikovaCena: 'listPrice',
  slevaPct: 'discountPct',
  platbaBezDph: 'paidExVat',
  jOverride: 'j',
  kOverride: 'k',
  lOverride: 'l',
  mOverride: 'm',
};

const porovnej = (a, b, smer) => {
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * smer;
  return String(a).localeCompare(String(b), 'cs') * smer;
};

/** Editovatelná buňka — commit na blur / Enter, jako v ostatních tabulkách systému. */
function CellEdit({ value, onCommit, disabled, step = '1', title, className = '' }) {
  return (
    <>
      <input
        key={value}
        type="number"
        step={step}
        className={`cell-edit no-print ${className}`}
        defaultValue={value}
        disabled={disabled}
        title={title}
        onBlur={(e) => onCommit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
      />
      {/* Na papíře se tiskne hodnota, ne políčko. `<input>` se umí zúžit pod
          svůj obsah, aniž to prohlížeč vezme v potaz při sázení tabulky,
          takže z „2700,00" vytiskl „2700,0" a částky vpravo chyběly. */}
      <span className="print-value">{String(value ?? '').replace('.', ',')}</span>
    </>
  );
}

/* Název tiskové sestavy. Prohlížeč jím pojmenuje PDF při „Uložit jako PDF",
   takže ve složce účetní je z názvu poznat sestava, obchodník, období i kdy
   vznikla: „statistiky Regvyd Dobias Karel 202606 vytvoreno 261016 130638".
   Diakritika a interpunkce jdou pryč — na disku a v e-mailu dělají potíže. */
const bezDiakritiky = (text) => (text || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^A-Za-z0-9 ]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const nazevSestavy = (obdobi, jmenoObchodnika) => {
  const d = new Date();
  const dva = (n) => String(n).padStart(2, '0');
  const datum = `${dva(d.getFullYear() % 100)}${dva(d.getMonth() + 1)}${dva(d.getDate())}`;
  const cas = `${dva(d.getHours())}${dva(d.getMinutes())}${dva(d.getSeconds())}`;
  return [
    'statistiky Regvyd',
    bezDiakritiky(jmenoObchodnika) || 'vsichni obchodnici',
    (obdobi || '').replace('/', ''),
    'vytvoreno', datum, cas,
  ].filter(Boolean).join(' ');
};

const toast = (message, variant = 'success') =>
  window.dispatchEvent(new CustomEvent('app:toast', { detail: { message, variant } }));

const PRAZDNA_RUCNI = {
  id: null, obchodnikId: '', titulId: '', zakaznik: '', cisloFaktury: '', cisloInzeratu: '',
  vydani: '', cenikovaCena: '', platbaBezDph: '', dpd: '0', slevaPct: '0',
  /* Ruční doplatek — provize bez základu (např. doplatek za vydání, jehož
     plnění plánu se dozvíme až později). */
  provize: '', poznamka: '',
};

function Statistics({ openTab }) {
  const [obdobiSeznam, setObdobiSeznam] = useState([]);
  const [obdobi, setObdobi] = useState('');
  /* Stav uzávěrky je na serveru (`commission_period`, migrace 049). */
  const [stavObdobi, setStavObdobi] = useState(null);
  const [otisk, setOtisk] = useState(null);
  const [prava, setPrava] = useState({ upravovat: true, uzavirat: true });
  const [obnovuji, setObnovuji] = useState(0);
  const [rows, setRows] = useState([]);
  /* Odvozené řádky ze serveru — superprovize manažera z týmu a mentoring
     z mentorovaného. Bloky jsou po lidech, komu se počítají. */
  const [odvozene, setOdvozene] = useState([]);
  const [nacitam, setNacitam] = useState(true);
  const [chyba, setChyba] = useState('');
  const [ciselniky, setCiselniky] = useState(null);

  /* filtry — jen období a obchodník */
  const [obchodnikId, setObchodnikId] = useState('');
  const [exportuje, setExportuje] = useState(false);

  /* Funkční updater `setRows` se vyhodnotí až při renderu, takže by uložení
     na server dostalo prázdnou hodnotu — proto ref na aktuální řádky. */
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const [razeni, setRazeni] = useState({ klic: null, smer: 1 });
  const [detailId, setDetailId] = useState(null);
  const [showRucni, setShowRucni] = useState(false);
  const [rucni, setRucni] = useState(PRAZDNA_RUCNI);

  const uzavreno = !!stavObdobi?.closed;
  const obchodnik = obchodnikId && ciselniky
    ? ciselniky.obchodnici.find(o => o.id === obchodnikId) || null
    : null;

  /* ------------------------------------------------------------ načtení -- */
  useEffect(() => {
    let zruseno = false;
    (async () => {
      try {
        const [obd, cis] = await Promise.all([nactiObdobi(), nactiCiselniky()]);
        if (zruseno) return;
        setObdobiSeznam(obd);
        setCiselniky(cis);
        /* Nejnovější období, za které vůbec existují uhrazené faktury. */
        if (obd.length) setObdobi(obd[0].id);
        else setNacitam(false);
      } catch (e) {
        if (!zruseno) { setChyba(e.message || 'Číselníky se nepodařilo načíst.'); setNacitam(false); }
      }
    })();
    return () => { zruseno = true; };
  }, []);

  useEffect(() => {
    if (!obdobi) return undefined;
    let zruseno = false;
    setNacitam(true);
    setChyba('');
    (async () => {
      try {
        const s = await nactiSestavu(obdobi);
        if (zruseno) return;
        PUVODNI = Object.fromEntries(s.polozky.map(r => [r.id, { ...r }]));
        setRows(s.polozky);
        setOdvozene(s.odvozene || []);
        setStavObdobi(s.stavObdobi);
        setOtisk(s.otisk);
        setPrava({ upravovat: s.muzeUpravovat, uzavirat: s.muzeUzavirat });
      } catch (e) {
        if (!zruseno) {
          setChyba(e.message || 'Sestavu se nepodařilo načíst.');
          setRows([]);
          setOdvozene([]);
        }
      } finally {
        if (!zruseno) setNacitam(false);
      }
    })();
    return () => { zruseno = true; };
  }, [obdobi, obnovuji]);

  /* --------------------------------------------------------------- data -- */
  /* Sestava se zobrazí až po výběru obchodníka — nikdy se nezobrazují
     položky ani součty za všechny obchodníky dohromady. */
  /* Bloky, které se počítají vybranému člověku. Jeden člověk jich může mít
     víc — manažer, který zároveň někoho mentoruje, dostane z jedné faktury
     obojí (rozhodnutí klienta). */
  const mojeOdvozene = useMemo(
    () => odvozene.filter(b => b.ozId === obchodnikId),
    [odvozene, obchodnikId],
  );

  const polozky = useMemo(() => {
    if (!obchodnikId || !ciselniky) return [];

    let rs = rows
      .filter(r => r.obdobi === obdobi && r.obchodnikId === obchodnikId)
      .map(r => dopocti(r, ciselniky.pasmoSplatnosti));

    /* Manažerovi patří do sestavy i faktury týmu, mentorovi faktury toho,
       koho zaučuje. */
    mojeOdvozene.forEach(b => {
      rs = rs.concat(b.polozky.map(r => dopoctiSuper(r, ciselniky.pasmoSplatnosti)));
    });

    if (razeni.klic) {
      rs = rs.slice().sort((a, b) => porovnej(a[razeni.klic], b[razeni.klic], razeni.smer));
    }
    return rs;
  }, [rows, mojeOdvozene, obdobi, obchodnikId, razeni, ciselniky]);

  const detail = detailId ? polozky.find(r => r.id === detailId) : null;

  /* -------------------------------------------------------------- akce --- */
  const handleSort = (klic) => {
    setRazeni(prev => ({ klic, smer: prev.klic === klic ? -prev.smer : 1 }));
  };

  /**
   * Zápis hodnoty do položky. `pct` = vstup je v procentech, ukládá se zlomek.
   *
   * Hodnota naskočí hned (lokální dopočet stejným vzorcem jako engine) a
   * zároveň se uloží na server. Když uložení selže, řádek se vrátí zpátky —
   * tiše neuložený přepis je horší než žádný.
   */
  const uprav = useCallback((id, pole, raw, { pct = false, nullable = false } = {}) => {
    const text = String(raw).replace(',', '.').trim();
    let v = text === '' ? null : Number(text);
    if (v != null && isNaN(v)) { toast('Neplatná hodnota.', 'warning'); return; }
    if (v == null && !nullable) v = 0;
    if (v != null && pct) v = v / 100;

    const puvodni = rowsRef.current.find(r => r.id === id);
    if (!puvodni) return;

    const next = { ...puvodni, [pole]: v };
    /* při změně slevy se dopočítá ceníková cena, ať sedí vazba na úhradu */
    if (pole === 'slevaPct') {
      next.cenikovaCena = Math.round(next.platbaBezDph / (1 - Math.min(0.95, (v || 0) / 100)));
    }
    /* `prepis` drží názvy, pod kterými přepis vrací server (`prelozPrepis`),
       aby se lokální zásah a ten načtený ze serveru poznaly stejně. */
    next.prepis = { ...(puvodni.prepis || {}), [POLE_UI[pole] || pole]: v };

    setRows(prev => prev.map(r => (r.id === id ? next : r)));

    const klic = POLE_PREPISU[pole];
    /* Ruční položka se neukládá jako přepis faktury — ta žádnou nemá. */
    if (!klic || next.rucni) return;

    const values = { [klic]: v };
    /* Sleva a ceníková cena drží jednu vazbu — ukládají se spolu, jinak by
       se procento na obrazovce rozešlo s částkou, ze které se počítá. */
    if (pole === 'slevaPct') values.listPrice = next.cenikovaCena;

    api.saveCommissionOverride(id, values, null)
      .then(({ data }) => {
        if (data?.locked) {
          toast(`Uzavřené období přepočítáno — verze ${data.version}, `
            + `${data.changedRows} ${data.changedRows === 1 ? 'řádek' : 'řádků'} jinak, `
            + `provize ${data.deltaCommission >= 0 ? '+' : ''}${fmtKc(data.deltaCommission)}.`, 'warning');
        }
      })
      .catch((e) => {
        setRows(prev => prev.map(r => (r.id === id ? puvodni : r)));
        toast(e.message || 'Přepis se nepodařilo uložit.', 'danger');
      });
  }, []);

  /** Zpět na automatický dopočet — smaže uložený přepis celého řádku. */
  const vratAutomaticky = async (id) => {
    try {
      await api.deleteCommissionOverride(id);
      toast('Řádek se vrátil k automatickému výpočtu.');
      setObnovuji(n => n + 1);
    } catch (e) {
      toast(e.message || 'Zrušení přepisu selhalo.', 'danger');
    }
  };

  const handleLock = async () => {
    try {
      if (uzavreno) {
        const { data } = await api.reopenCommissionPeriod(naApi(obdobi));
        setStavObdobi(data.periodState);
        toast(`Období ${obdobi} bylo znovu otevřeno.`, 'warning');
      } else {
        const { data } = await api.closeCommissionPeriod(naApi(obdobi));
        setStavObdobi(data.periodState);
        toast(`Období ${obdobi} uzavřeno — ${data.rowCount} řádků, provize ${fmtKc(data.commissionTotal)}.`);
      }
      setObnovuji(n => n + 1);
    } catch (e) {
      toast(e.message || 'Změna stavu období selhala.', 'danger');
    }
  };

  /**
   * Ruční položka (`commission_manual_item`, migrace 049).
   *
   * Pásmo i bonus si dopočítá engine podle titulu — obrazovka posílá jen to,
   * co zadal uživatel, a sestavu si pak natáhne znovu.
   */
  const handlePridatRucni = async () => {
    const cislo = (x) => Number(String(x ?? '').replace(',', '.')) || 0;
    const castka = cislo(rucni.platbaBezDph);
    const provize = cislo(rucni.provize);
    if (!rucni.obchodnikId) { toast('Vyberte obchodníka.', 'warning'); return; }
    if (!rucni.titulId && !provize) { toast('Vyberte titul, nebo zadejte rovnou částku provize.', 'warning'); return; }
    if (!castka && !provize) { toast('Zadejte úhradu bez DPH, nebo částku provize.', 'warning'); return; }

    try {
      await api.saveCommissionManualItem({
        period: naApi(obdobi),
        salespersonId: Number(rucni.obchodnikId),
        titleId: rucni.titulId ? Number(rucni.titulId) : null,
        customerName: rucni.zakaznik || null,
        invoiceNumber: rucni.cisloFaktury || null,
        bookingItemNumber: rucni.cisloInzeratu || null,
        issueName: rucni.vydani || null,
        listPrice: cislo(rucni.cenikovaCena) || castka,
        paidExVat: castka,
        dpd: cislo(rucni.dpd),
        discountPct: cislo(rucni.slevaPct),
        m: provize || null,
        note: rucni.poznamka || null,
      }, rucni.id || null);

      setShowRucni(false);
      setRucni(PRAZDNA_RUCNI);
      if (rucni.obchodnikId !== obchodnikId) setObchodnikId(rucni.obchodnikId);
      toast('Ruční položka uložena.');
      setObnovuji(n => n + 1);
    } catch (e) {
      toast(e.message || 'Ruční položku se nepodařilo uložit.', 'danger');
    }
  };

  /** Otevře dialog ruční položky — prázdný, nebo předvyplněný existující. */
  const otevriRucni = (radek = null) => {
    if (!radek) { setRucni({ ...PRAZDNA_RUCNI, obchodnikId }); setShowRucni(true); return; }
    setRucni({
      id: radek.rucniId,
      obchodnikId: radek.obchodnikId,
      titulId: radek.radky?.[0]?.titulId || '',
      zakaznik: radek.zakaznik === '(neuvedeno)' ? '' : (radek.zakaznik || ''),
      cisloFaktury: radek.cisloFaktury === 'Ruční položka' ? '' : (radek.cisloFaktury || ''),
      cisloInzeratu: radek.inzeraty?.[0] || '',
      vydani: radek.vydaniSeznam || '',
      cenikovaCena: radek.cenikovaCena || '',
      platbaBezDph: radek.platbaBezDph || '',
      dpd: radek.dpd ?? 0,
      slevaPct: radek.slevaPct ?? 0,
      provize: radek.mOverride ?? '',
      poznamka: radek.poznamka || '',
    });
    setShowRucni(true);
  };

  const handleSmazat = async (radek) => {
    if (!radek.rucniId) { toast('Tahle položka ručně vložená není.', 'warning'); return; }
    if (!window.confirm('Opravdu smazat ručně vloženou položku?')) return;
    try {
      await api.deleteCommissionManualItem(radek.rucniId);
      toast('Položka smazána.');
      setObnovuji(n => n + 1);
    } catch (e) {
      toast(e.message || 'Smazání selhalo.', 'danger');
    }
  };

  /* Export do Excelu — stejná sestava, jaká je na obrazovce, a stejný název
     souboru jako u tisku. Jde kdykoliv, i nad uzavřeným obdobím (to se vysází
     z otisku). Bez vybraného obchodníka se exportuje celé období. */
  const handleExport = async () => {
    if (exportuje) return;
    setExportuje(true);
    try {
      const nazev = nazevSestavy(obdobi, obchodnik?.jmeno);
      const { blob, filename } = await api.exportStatisticsToXlsx(
        naApi(obdobi),
        obchodnikId || null,
        nazev,
      );
      downloadBlob(blob, filename);
      toast('Excel vygenerován.');
    } catch (e) {
      toast(e.message || 'Export se nepodařil.', 'danger');
    } finally {
      setExportuje(false);
    }
  };

  /* Tisk: název dokumentu je zároveň výchozí název PDF, které prohlížeč uloží. */
  const handlePrint = () => {
    const puvodni = document.title;
    document.title = nazevSestavy(obdobi, obchodnik?.jmeno);
    const obnov = () => {
      document.title = puvodni;
      window.removeEventListener('afterprint', obnov);
    };
    window.addEventListener('afterprint', obnov);
    // Záloha pro prohlížeče, které `afterprint` nepošlou (starší Safari).
    setTimeout(obnov, 60000);
    window.print();
  };

  /* Šířky sloupců — tažení za okraj hlavičky, uložené u uživatele v DB. */
  const { wrapRef, colgroup, tableStyle, resizerProps, maUlozene, vychozi } =
    useColumnWidths('statistics.commissions', SLOUPCE_KLICE, SLOUPCE_MIN);

  const SortIcon = ({ klic }) => (
    <i className={`bi ${razeni.klic === klic
      ? (razeni.smer > 0 ? 'bi-sort-up' : 'bi-sort-down')
      : 'bi-arrow-down-up'} sort-ind`} />
  );

  const zakladCelkem = soucet(polozky, r => r.platbaBezDph);
  const provizeCelkem = soucet(polozky, r => r.m);
  const superprovizeCelkem = soucet(polozky.filter(r => r.super && r.druh !== 'mentor'), r => r.m);
  const mentoringCelkem = soucet(polozky.filter(r => r.druh === 'mentor'), r => r.m);
  const vlastniCelkem = provizeCelkem - superprovizeCelkem - mentoringCelkem;
  const tymBlok = mojeOdvozene.find(b => b.druh === 'super') || null;
  const mentorBloky = mojeOdvozene.filter(b => b.druh === 'mentor');

  /* -------------------------------------------------------------- render - */
  if (nacitam && !rows.length) {
    return (
      <Container fluid className="px-4 py-5 text-center statistics-module">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 statistics-module">
      {chyba && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setChyba('')}>{chyba}</Alert>
      )}

      {/* Hlavička */}
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center g-2">
            <div className="col-md-5">
              <h4 className="mb-0"><i className="bi bi-graph-up me-2"></i>Provize</h4>
            </div>
            <div className="col-md-7 text-md-end no-print">
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={handlePrint}>
                <i className="bi bi-printer me-1"></i>Tisková sestava
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2"
                onClick={() => openTab(
                  'statistics-settings',
                  'Nastavení sazeb',
                  '/statistics/settings',
                  'StatisticsSettings',
                )}
              >
                <i className="bi bi-sliders me-1"></i>Nastavení
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                className="me-2"
                onClick={handleExport}
                disabled={exportuje}
              >
                <i className={`bi ${exportuje ? 'bi-hourglass-split' : 'bi-file-earmark-spreadsheet'} me-1`}></i>
                {exportuje ? 'Exportuji…' : 'Export XLSX'}
              </Button>
              <Button variant="primary" size="sm" onClick={handleLock} disabled={!prava.uzavirat}>
                <i className={`bi ${uzavreno ? 'bi-unlock' : 'bi-lock'} me-1`}></i>
                {uzavreno ? 'Znovu otevřít období' : 'Uzavřít období'}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <div className="table-container">
        {/* Filtry */}
        <div className="row g-3 align-items-end mb-4 no-print">
          <div className="col-6 col-md-2">
            <label className="form-label">Období</label>
            <Form.Select value={obdobi} onChange={(e) => setObdobi(e.target.value)}>
              {obdobiSeznam.map(o => (
                <option key={o.id} value={o.id}>{o.id} ({o.pocetFaktur} faktur)</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Obchodník</label>
            <SearchableSelect
              options={(ciselniky?.obchodnici || []).map(o => ({ id: o.id, label: o.jmeno }))}
              value={obchodnikId}
              onChange={(id) => { setObchodnikId(id); setDetailId(null); }}
              placeholder="Hledat obchodníka…"
              emptyLabel="Vyberte obchodníka"
            />
          </div>
          {/* Stav uzávěrky — vedle filtrů, ne ve vlastním pruhu nad tabulkou:
              sestava je vysoká a každý řádek navíc ubírá viditelné faktury. */}
          <div className="col-12 col-md-auto">
            <div
              className={`lock-strip ${uzavreno ? 'locked' : ''}`}
              title={uzavreno
                ? [`Uzavřel ${stavObdobi?.closedBy || '—'} ${stavObdobi?.closedAt || ''}`,
                   otisk ? `otisk verze ${otisk.version}` : null].filter(Boolean).join(' · ')
                : 'Sestava se počítá živě z aktuálních dat.'}
            >
              <i className={`bi ${uzavreno ? 'bi-lock-fill' : 'bi-unlock'} ls-icon`}></i>
              <strong>Období {obdobi} je {uzavreno ? 'uzavřené' : 'otevřené'}.</strong>
              {uzavreno && otisk?.version > 1 && (
                <span className="small-muted">otisk v{otisk.version}</span>
              )}
            </div>
          </div>
        </div>

        {/* Hlavička tiskové sestavy — na obrazovce skrytá. Nahrazuje celou horní
            část stránky, z papíru má být na první pohled jasné jen to, co je
            to za sestavu, za koho, za jaké období a kdy vznikla. */}
        <div className="print-head">
          <strong>Provize</strong>
          {obchodnik && <> · {obchodnik.jmeno}</>}
          {' · období '}{obdobi}
          <span className="ph-kdy">vytištěno {new Date().toLocaleString('cs-CZ')}</span>
        </div>

        {/* Lišta nad tabulkou — dává smysl až s vybraným obchodníkem */}
        {obchodnik && (
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 no-print">
            <div className="fw-semibold">
              <i className="bi bi-person-badge me-2"></i>{obchodnik.jmeno}
              <span className="small-muted"> · {obdobi}</span>
            </div>
            <div className="d-flex gap-2">
              {maUlozene && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="no-print"
                  title="Vrátit šířky sloupců do původního stavu"
                  onClick={() => { vychozi(); toast('Šířky sloupců vráceny na výchozí.'); }}
                >
                  <i className="bi bi-arrows-angle-contract me-1"></i>Výchozí šířky
                </Button>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                className="no-print"
                disabled={!prava.upravovat}
                onClick={() => otevriRucni()}
              >
                <i className="bi bi-plus-lg me-1"></i>Ruční položka
              </Button>
            </div>
          </div>
        )}

        {!obchodnikId ? (
          <div className="empty-state">
            <i className="bi bi-person-badge"></i>
            Vyberte obchodníka, pro kterého chcete sestavu provizí zobrazit.
          </div>
        ) : polozky.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-inbox"></i>
            Vybraný obchodník nemá v období {obdobi} žádné položky.
          </div>
        ) : (
          <>
            <div className="table-responsive stat-table-wrap" ref={wrapRef}>
              <Table hover className="align-middle stat-table" style={tableStyle}>
                {colgroup && (
                  <colgroup>
                    {colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                  </colgroup>
                )}
                <thead className="table-light">
                  <tr>
                    {SLOUPCE.map(sl => (
                      <th
                        key={sl.klic}
                        role={sl.neradit ? undefined : 'button'}
                        className={[sl.num ? 'num' : '', sl.akce ? 'text-end no-print' : ''].filter(Boolean).join(' ')}
                        // Úzký sloupec popisek ořízne, tohle ho dá přečíst
                        title={sl.popisek || undefined}
                        onClick={sl.neradit ? undefined : () => handleSort(sl.klic)}
                      >
                        <span className="th-text">
                          {sl.popisek}
                          {!sl.neradit && <> <SortIcon klic={sl.klic} /></>}
                        </span>
                        <span {...resizerProps(sl.klic)} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {polozky.map(r => {
                    const badge = TYP_BADGE[r.typMedia] || TYP_BADGE.nase;
                    return (
                      <tr key={r.id} className={[
                        r.rucni ? 'row-manual' : '',
                        r.super ? (r.druh === 'mentor' ? 'row-mentor' : 'row-super') : '',
                      ].filter(Boolean).join(' ')}>
                        <td>
                          <div className="text-truncate" title={r.obchodnik}>{r.obchodnik || '—'}</div>
                          {r.super && (r.druh === 'mentor' ? (
                            <span className="badge-soft b-mentor" title={`Mentoring ${fmtPct(r.superPct)} z úhrady mentorovaného — nekrátí se za splatnost`}>
                              <i className="bi bi-mortarboard"></i>mentoring {fmtPct(r.superPct)}
                            </span>
                          ) : (
                            <span className="badge-soft b-super" title={`Superprovize ${fmtPct(r.superPct)} z úhrady podřízeného — nekrátí se za splatnost`}>
                              <i className="bi bi-diagram-3"></i>superprovize {fmtPct(r.superPct)}
                            </span>
                          ))}
                        </td>
                        <td className="mono">{r.cisloFaktury}</td>
                        <td>
                          <div className="text-truncate" title={r.zakaznik}>{r.zakaznik}</div>
                          {r.inzerent !== r.zakaznik && (
                            <div className="small-muted text-truncate" style={{ fontSize: '.72rem' }} title={r.inzerent}>
                              <span className="badge-soft b-neutral me-1">agentura</span>{r.inzerent}
                            </div>
                          )}
                          {r.rucni && (
                            <span className="badge-soft b-manual mt-1"><i className="bi bi-pencil-square"></i>ruční</span>
                          )}
                          {r.poznamka && (
                            <div className="small-muted" style={{ fontSize: '.72rem' }} title={r.poznamka}>
                              <i className="bi bi-sticky me-1"></i>{r.poznamka}
                            </div>
                          )}
                        </td>
                        <td className="mono">{r.cisloInzeratu}</td>
                        <td>
                          <div className="text-truncate" title={r.vydaniSeznam || r.vydaniNazev}>{r.vydaniNazev}</div>
                          <span className="small-muted" style={{ fontSize: '.72rem' }}>{r.typInzerce}</span>
                        </td>
                        <td className="small-muted">{r.mutaceNazev || '—'}</td>
                        <td>
                          <span className={`badge-soft ${badge.cls}`}><i className={`bi ${badge.ikona}`}></i>{badge.label}</span>
                        </td>
                        <td className={`num editable ${zmeneno(r, 'dpd') ? 'overridden' : ''}`}>
                          <CellEdit
                            value={r.dpd}
                            disabled={!prava.upravovat || r.rucni || r.super}
                            title={titulekZmeny(r, 'dpd', (v) => `${v} d`)
                              || `${r.pasmo.nazev} · koeficient ${r.koef}`}
                            onCommit={(v) => uprav(r.id, 'dpd', v)}
                          />
                        </td>
                        <td className={`num editable ${zmeneno(r, 'cenikovaCena') ? 'overridden' : ''}`}>
                          <CellEdit
                            value={Math.round(r.cenikovaCena)}
                            disabled={!prava.upravovat || r.rucni || r.super}
                            title={titulekZmeny(r, 'cenikovaCena', fmtKc)}
                            onCommit={(v) => uprav(r.id, 'cenikovaCena', v)}
                          />
                        </td>
                        <td className={`num editable ${zmeneno(r, 'slevaPct') ? 'overridden' : ''}`}>
                          <CellEdit
                            value={Math.round((r.slevaPct || 0) * 100) / 100}
                            step="0.1"
                            disabled={!prava.upravovat || r.rucni || r.super}
                            title={titulekZmeny(r, 'slevaPct', fmtPctRaw)}
                            onCommit={(v) => uprav(r.id, 'slevaPct', v)}
                          />
                        </td>
                        <td className={`num editable ${zmeneno(r, 'platbaBezDph') ? 'overridden' : ''}`}>
                          <CellEdit
                            value={r.platbaBezDph.toFixed(2)}
                            step="0.01"
                            disabled={!prava.upravovat || r.rucni || r.super}
                            title={titulekZmeny(r, 'platbaBezDph', fmtKc)}
                            onCommit={(v) => uprav(r.id, 'platbaBezDph', v)}
                          />
                        </td>
                        <td className={`num editable ${r.jOverride != null ? 'overridden' : ''}`}>
                          <CellEdit
                            value={(r.j * 100).toFixed(1)}
                            step="0.1"
                            disabled={!prava.upravovat || r.rucni || r.super}
                            title={r.jOverride != null
                              ? [`Ručně přepsáno · dopočet byl ${fmtPct(r.jAuto)}`, podpisPrepisu(r)].filter(Boolean).join(' · ')
                              : ''}
                            onCommit={(v) => uprav(r.id, 'jOverride', v, { pct: true, nullable: true })}
                          />
                        </td>
                        <td className={`num editable ${r.kOverride != null ? 'overridden' : ''}`}>
                          <CellEdit
                            value={(r.k * 100).toFixed(1)}
                            step="0.1"
                            disabled={!prava.upravovat || r.rucni || r.super}
                            title={r.kOverride != null
                              ? [`Ručně přepsáno · dopočet byl ${fmtPct(r.kAuto)}`, podpisPrepisu(r)].filter(Boolean).join(' · ')
                              : ''}
                            onCommit={(v) => uprav(r.id, 'kOverride', v, { pct: true, nullable: true })}
                          />
                        </td>
                        <td className={`num editable ${r.lOverride != null ? 'overridden' : ''}`}>
                          <CellEdit
                            value={(r.l * 100).toFixed(1)}
                            step="0.1"
                            disabled={!prava.upravovat || r.rucni || r.super}
                            title={`(${fmtPct(r.j)} + ${fmtPct(r.k)}) × ${String(r.koef).replace('.', ',')} = ${fmtPct(r.lAuto)}`}
                            onCommit={(v) => uprav(r.id, 'lOverride', v, { pct: true, nullable: true })}
                          />
                        </td>
                        <td className={`num editable ${r.mOverride != null ? 'overridden' : ''}`}>
                          <CellEdit
                            value={r.m.toFixed(2)}
                            step="0.01"
                            disabled={!prava.upravovat || r.rucni || r.super}
                            title={`úhrada × výsledné % = ${fmtKc(r.mAuto)}`}
                            onCommit={(v) => uprav(r.id, 'mOverride', v, { nullable: true })}
                          />
                        </td>
                        <td className="text-end text-nowrap no-print">
                          <Button
                            variant="outline-secondary"
                            className="btn-action me-1"
                            title="Rozpad výpočtu"
                            onClick={() => setDetailId(r.id)}
                          >
                            <i className="bi bi-calculator"></i>
                          </Button>
                          {maPrepis(r.prepis) && !r.rucni && !r.super && (
                            <Button
                              variant="outline-warning"
                              className="btn-action me-1"
                              title="Vrátit řádek k automatickému výpočtu"
                              disabled={!prava.upravovat}
                              onClick={() => vratAutomaticky(r.id)}
                            >
                              <i className="bi bi-arrow-counterclockwise"></i>
                            </Button>
                          )}
                          {r.rucni && (
                            <>
                              <Button
                                variant="outline-secondary"
                                className="btn-action me-1"
                                title="Upravit ruční položku"
                                disabled={!prava.upravovat}
                                onClick={() => otevriRucni(r)}
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                className="btn-action"
                                title="Smazat ruční položku"
                                disabled={!prava.upravovat}
                                onClick={() => handleSmazat(r)}
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={7}>
                      CELKEM {fmtNum(polozky.length)} faktur
                      {!!mojeOdvozene.length && (
                        <span className="small-muted ms-2">
                          (z toho
                          {!!tymBlok && ` superprovize ${fmtKc(superprovizeCelkem)} z ${fmtNum(tymBlok.pocetPodrizenych)} podřízených;`}
                          {!!mentorBloky.length && ` mentoring ${fmtKc(mentoringCelkem)} (${mentorBloky.map(b => b.mentorovany).join(', ')});`}
                          {` vlastní provize ${fmtKc(vlastniCelkem)})`}
                        </span>
                      )}
                      <span className="legenda no-print">
                        <span className="badge-soft b-manual"><i className="bi bi-pencil-square"></i>ruční položka</span>
                        <span className="badge-soft b-warn"><i className="bi bi-pencil-fill"></i>přepsaná hodnota</span>
                        {!!tymBlok && (
                          <span className="badge-soft b-super"><i className="bi bi-diagram-3"></i>superprovize z podřízeného</span>
                        )}
                        {!!mentorBloky.length && (
                          <span className="badge-soft b-mentor"><i className="bi bi-mortarboard"></i>mentoring</span>
                        )}
                      </span>
                    </td>
                    <td></td>
                    <td className="num">{fmtKc(soucet(polozky, r => r.cenikovaCena))}</td>
                    <td></td>
                    <td className="num">{fmtKc(zakladCelkem)}</td>
                    <td></td>
                    <td></td>
                    <td className="num">{fmtPct(zakladCelkem ? provizeCelkem / zakladCelkem : 0)}</td>
                    <td className="num">{fmtKc(provizeCelkem)}</td>
                    <td className="no-print"></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* Rozpad výpočtu */}
      <Offcanvas
        show={!!detail}
        onHide={() => setDetailId(null)}
        placement="end"
        className="stat-detail-canvas"
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <div>
            <Offcanvas.Title className="h5 mb-0">Rozpad výpočtu provize</Offcanvas.Title>
            {detail && (
              <div className="small-muted">
                {detail.super ? detail.obchodnik : obchodnik?.jmeno} · {detail.cisloInzeratu} · {detail.vydaniNazev}
              </div>
            )}
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {detail && (
            <>
              {detail.super && (
                <div className={`alert py-2 small ${detail.druh === 'mentor' ? 'alert-info' : 'alert-success'}`}>
                  <i className={`bi ${detail.druh === 'mentor' ? 'bi-mortarboard' : 'bi-diagram-3'} me-1`}></i>
                  <strong>{detail.druh === 'mentor' ? 'Mentoring. ' : 'Superprovize z podřízeného. '}</strong>
                  Faktura patří <strong>{detail.obchodnik}</strong>; {obchodnik?.jmeno} z ní bere
                  {' '}{fmtPct(detail.superPct)} z úhrady bez DPH, tedy <strong>{fmtKc(detail.m)}</strong>.
                  Sazby „% z vydání" a „% bonus" jsou provize obchodníka — jen pro informaci.
                  Za opožděnou platbu se {detail.druh === 'mentor' ? 'mentoring' : 'superprovize'} nekrátí.
                </div>
              )}
              <div className="calc-flow mb-3">
                <div className="calc-chip">
                  <div className="cc-label">Úhrada bez DPH</div>
                  <div className="cc-value">{fmtKc(detail.platbaBezDph)}</div>
                </div>
                <div className="calc-chip op">×</div>
                <div className="calc-chip">
                  <div className="cc-label">% z vydání</div>
                  <div className="cc-value">{fmtPct(detail.j)}</div>
                </div>
                <div className="calc-chip op">+</div>
                <div className="calc-chip">
                  <div className="cc-label">% bonus</div>
                  <div className="cc-value">{fmtPct(detail.k)}</div>
                </div>
              </div>
              <div className="calc-flow mb-3">
                <div className="calc-chip op" style={{ minWidth: 0 }}>↳</div>
                <div className={`calc-chip ${detail.koef < 1 ? 'cut' : 'muted-chip'}`}>
                  <div className="cc-label">Korekce splatnosti</div>
                  <div className="cc-value">× {String(detail.koef).replace('.', ',')}</div>
                </div>
                <div className="calc-chip op">=</div>
                <div className="calc-chip result">
                  <div className="cc-label">Výsledné %</div>
                  <div className="cc-value">{fmtPct(detail.l)}</div>
                </div>
                <div className="calc-chip op">→</div>
                <div className="calc-chip result">
                  <div className="cc-label">Provize</div>
                  <div className="cc-value">{fmtKc(detail.m)}</div>
                </div>
              </div>

              {detail.koef < 1 && !detail.super && (
                <div className="alert alert-danger py-2 small">
                  <i className="bi bi-scissors me-1"></i>
                  Úhrada přišla <strong>{detail.dpd} dní</strong> po splatnosti (pásmo {detail.pasmo.nazev}) →
                  krácení {detail.koef === 0.5 ? '50 %' : '100 %'}. Nevyplaceno <strong>{fmtKc(detail.ztrataKracenim)}</strong>.
                </div>
              )}

              {detail.prepsano && (
                <div className="alert alert-warning py-2 small">
                  <i className="bi bi-pencil-fill me-1"></i><strong>Ruční přepis. </strong>
                  {detail.jOverride != null && `% z vydání: dopočet ${fmtPct(detail.jAuto)} → přepsáno ${fmtPct(detail.j)}. `}
                  {detail.kOverride != null && `% bonus: dopočet ${fmtPct(detail.kAuto)} → přepsáno ${fmtPct(detail.k)}. `}
                  {detail.lOverride != null && `Výsledné %: dopočet ${fmtPct(detail.lAuto)} → přepsáno ${fmtPct(detail.l)}. `}
                  {detail.mOverride != null && `Provize: dopočet ${fmtKc(detail.mAuto)} → přepsáno ${fmtKc(detail.m)}. `}
                </div>
              )}

              {detail.rucni && (
                <div className="alert alert-primary py-2 small">
                  <i className="bi bi-pencil-square me-1"></i><strong>Ručně vložená položka. </strong>
                  {detail.poznamka || 'Bez poznámky.'}
                </div>
              )}

              <div className="sec-title mt-3">Údaje faktury</div>
              <Table size="sm" className="small mb-0">
                <tbody>
                  <tr><td className="small-muted">Faktura</td><td className="mono">{detail.cisloFaktury}</td></tr>
                  <tr><td className="small-muted">Zákazník</td><td>{detail.zakaznik}</td></tr>
                  {detail.inzerent !== detail.zakaznik && (
                    <tr><td className="small-muted">Inzerent</td><td>{detail.inzerent}</td></tr>
                  )}
                  <tr><td className="small-muted">Inzeráty</td><td className="mono">{detail.inzeraty && detail.inzeraty.length ? detail.inzeraty.join(', ') : detail.cisloInzeratu}</td></tr>
                  <tr>
                    <td className="small-muted">Vydání / mutace</td>
                    <td>{detail.vydaniNazev}{detail.mutaceNazev ? ` · ${detail.mutaceNazev}` : ''}</td>
                  </tr>
                  <tr>
                    <td className="small-muted">Typ média</td>
                    <td>
                      <span className={`badge-soft ${(TYP_BADGE[detail.typMedia] || TYP_BADGE.nase).cls}`}>
                        {(TYP_BADGE[detail.typMedia] || TYP_BADGE.nase).label}
                      </span>
                    </td>
                  </tr>
                  <tr><td className="small-muted">Ceníková cena</td><td>{fmtKc(detail.cenikovaCena)}</td></tr>
                  <tr><td className="small-muted">Sleva</td><td>{fmtPctRaw(detail.slevaPct)}</td></tr>
                  <tr><td className="small-muted">Sazba DPH</td><td>{detail.sazbaDph == null ? 'různé' : fmtPct(detail.sazbaDph, 0)}</td></tr>
                  <tr>
                    <td className="small-muted">Dní po splatnosti</td>
                    <td>
                      <span className={`dpd-chip ${detail.pasmo.kod}`} title={`${detail.pasmo.nazev} · ${detail.pasmo.popis}`}>
                        <i className={`bi ${detail.pasmo.ikona}`}></i>{detail.dpd} d
                      </span>
                    </td>
                  </tr>
                </tbody>
              </Table>
            {detail.radky && detail.radky.length > 0 && (
                <>
                  <div className="sec-title mt-3">Položky faktury ({detail.radky.length})</div>
                  <div className="small-muted mb-2" style={{ fontSize: '.78rem' }}>
                    Provize se počítá po položkách — každá patří do svého vydání a má vlastní pásmo.
                    Sazby na řádku faktury jsou průměry vážené úhradou.
                  </div>
                  <Table size="sm" className="small mb-0">
                    <thead>
                      <tr>
                        <th>Položka</th>
                        <th className="num">Úhrada</th>
                        <th className="num">% vyd.</th>
                        <th className="num">% bonus</th>
                        <th className="num">Provize</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.radky.map(l => (
                        <tr key={l.id} className={l.slevovy ? 'small-muted' : ''}>
                          <td>
                            <div className="mono">{l.cislo}</div>
                            <div className="text-truncate" style={{ maxWidth: '220px' }} title={l.nazev}>
                              {l.slevovy ? l.nazev : (l.vydani || l.nazev)}
                            </div>
                            {!l.slevovy && l.jDuvod && (
                              <div className="small-muted" style={{ fontSize: '.7rem' }}>{l.jDuvod}</div>
                            )}
                          </td>
                          <td className="num">{fmtKc(l.platbaBezDph)}</td>
                          <td className="num">{fmtPct(l.j)}</td>
                          <td className="num">{fmtPct(l.k)}</td>
                          <td className="num">{fmtKc(l.m)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Ruční položka */}
      <Modal show={showRucni} onHide={() => setShowRucni(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="h5"><i className="bi bi-plus-square me-2"></i>{rucni.id ? 'Úprava ruční položky' : 'Nová ruční položka'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Obchodník <span className="text-danger">*</span></label>
              <Form.Select
                value={rucni.obchodnikId}
                onChange={(e) => setRucni({ ...rucni, obchodnikId: e.target.value })}
              >
                <option value="">— vyberte —</option>
                {(ciselniky?.obchodnici || []).map(o => <option key={o.id} value={o.id}>{o.jmeno}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Titul</label>
              <Form.Select
                value={rucni.titulId}
                onChange={(e) => setRucni({ ...rucni, titulId: e.target.value })}
              >
                <option value="">— vyberte —</option>
                {(ciselniky?.tituly || []).map(t => <option key={t.id} value={t.id}>{t.nazev}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Zákazník</label>
              <Form.Control
                placeholder="Název firmy nebo osoby"
                value={rucni.zakaznik}
                onChange={(e) => setRucni({ ...rucni, zakaznik: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Číslo faktury</label>
              <Form.Control
                placeholder="—"
                value={rucni.cisloFaktury}
                onChange={(e) => setRucni({ ...rucni, cisloFaktury: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Číslo inzerátu</label>
              <Form.Control
                placeholder="RUČNÍ"
                value={rucni.cisloInzeratu}
                onChange={(e) => setRucni({ ...rucni, cisloInzeratu: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Ceníková cena (Kč)</label>
              <Form.Control
                type="number"
                value={rucni.cenikovaCena}
                onChange={(e) => setRucni({ ...rucni, cenikovaCena: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Úhrada bez DPH (Kč)</label>
              <Form.Control
                type="number"
                value={rucni.platbaBezDph}
                onChange={(e) => setRucni({ ...rucni, platbaBezDph: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Vydání</label>
              <Form.Control
                placeholder="Např. Klíč 2026/09 — jen popisek do sestavy"
                value={rucni.vydani}
                onChange={(e) => setRucni({ ...rucni, vydani: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Provize (Kč)</label>
              <Form.Control
                type="number"
                placeholder="dopočítá se"
                value={rucni.provize}
                onChange={(e) => setRucni({ ...rucni, provize: e.target.value })}
              />
              <div className="form-text">
                Ruční doplatek — provize bez základu. Vyplň, když se nemá počítat ze sazeb.
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Dní po splatnosti</label>
              <Form.Control
                type="number"
                value={rucni.dpd}
                onChange={(e) => setRucni({ ...rucni, dpd: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Sleva (%)</label>
              <Form.Control
                type="number"
                step="0.1"
                value={rucni.slevaPct}
                onChange={(e) => setRucni({ ...rucni, slevaPct: e.target.value })}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Poznámka / důvod ruční položky</label>
              <Form.Control
                placeholder="např. dorovnání chyby v exportu, dělená položka…"
                value={rucni.poznamka}
                onChange={(e) => setRucni({ ...rucni, poznamka: e.target.value })}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRucni(false)}>Zrušit</Button>
          <Button variant="primary" onClick={handlePridatRucni}>
            <i className="bi bi-check-lg me-1"></i>Přidat položku
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Statistics;
